/*
 * Copyright 2017-present Evernote Corporation. All rights reserved.
 */

import DoublyLinkedList, { Node } from 'ion-structures/doublylinkedlist';
import { Subscriber } from '../types';

/*
 * A list of subscribers, extended from our standard linked list implementation
 */

class SubscriberList extends DoublyLinkedList<Subscriber> {
  /**
   * Similar to the standard push method, but allows for fast unsubscribe by
   * returning a function that, when called, will slice the node out of
   * the list
   * @param {Subscriber} subscriber
   * @return {Function}
   */
  subscribe(subscriber: Subscriber): () => void {
    const n = new Node(subscriber);

    // Special case: list is empty
    if (this.head == null && this.tail == null) {
      this.head = n;
      this.tail = n;
      this.length++;
      return () => {
        this.unsubscribe(n);
      };
    }

    if (this.tail == null) {
      throw new Error('cannot subscribe: tail is unexpectedly null');
    }

    const currentLast = this.tail;
    currentLast.next = n;
    n.prev = currentLast;
    this.tail = n;
    this.length++;

    return () => {
      this.unsubscribe(n);
    };
  }

  /**
   * Removes a subscriber from the list
   * @param {Node<Subscriber>} subscriber
   * @return {void}
   */
  unsubscribe(subscriber: Node<Subscriber>): void {
    this.sliceNode(subscriber);
  }
}

export default SubscriberList;
